import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Popover } from '@mui/material';
import { MinLoader, SimpleTooltip } from 'components';
import { Images, FindLoad, useSecondaryModal, FindSuccessItem, apptIssueType } from 'utils';
import { appointmentActions, httpRequestsOnSuccessActions } from 'store';
import { renderApptStateIssues } from './constants';
import { ScheduleDetails } from './scheduleDetails';

const REFRESH_ACTION = 'REFRESH_ERRORS';
const warningList = [{ text: 'Appointment was removed from claim' }];
export const ApptStateInfo = ({ refresh, appt, related }) => {
    const issue = appt?.issues;
    // Notes are no longer embedded on the appt (Phase 6b cutover) — the appt now
    // carries denormalized counts. The badge total = clinical (session) + office.
    const clinicalNotesCount = appt?.noteCounts?.session?.total || 0;
    const officeNotesCount = appt?.noteCounts?.office?.total || 0;
    const notesCount = clinicalNotesCount + officeNotesCount;
    const dispatch = useDispatch();
    const [anchorEl, setAnchorEl] = useState(null);
    const [type, setType] = useState(null);
    const openAnchor = Boolean(anchorEl);
    const id = openAnchor ? 'simple-popover' : undefined;
    const loader = FindLoad(REFRESH_ACTION);
    const success = FindSuccessItem(REFRESH_ACTION);
    const [selectedId, setSelectedId] = useState(null);
    const { openSecondary } = useSecondaryModal();

    useEffect(() => {
        if (success && selectedId === appt?.id) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(REFRESH_ACTION));
            setSelectedId(null);
        }
    }, [success]);
    const handleClick = (event, issueType) => {
        setType(issueType);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setAnchorEl(null);
    };

    const refreshError = () => {
        setSelectedId(appt?.id);
        dispatch(appointmentActions.refreshErrors(appt?.id));
    };

    return (
        <div>
            <div className="flex-align-center" style={{ gap: 6 }}>
                {notesCount > 0 && (
                    <button
                        type="button"
                        aria-describedby={id}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClick(e, 'notes');
                        }}
                        className="appt-table-note-box"
                    >
                        <div className="appt-table-note-info">
                            <img src={Images.tableNotes} alt="alert" />
                            <p>{notesCount}</p>
                        </div>
                    </button>
                )}
                {appt?.removedFromClaimFlag && (
                    <button
                        type="button"
                        aria-describedby={id}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClick(e, 'warning');
                        }}
                        className="appt-table-warning-box"
                    >
                        <div className="appt-table-warning-info">
                            <img src={Images.alertWarning} alt="alert" />
                            <p>{1}</p>
                        </div>
                    </button>
                )}
                {!!issue?.length > 0 && (
                    <button
                        type="button"
                        aria-describedby={id}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClick(e, 'error');
                        }}
                        className="appt-table-error-box"
                    >
                        <div className="appt-table-error-info">
                            <img src={Images.alertError} alt="alert" />
                            <p>{issue?.length}</p>
                        </div>
                    </button>
                )}
                {!!related?.length > 0 && (
                    <SimpleTooltip title={'Related Appointments'} placement="bottom" arrow>
                        <button
                            type="button"
                            aria-describedby={id}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleClick(e, 'related');
                            }}
                            className="appt-table-related-box"
                        >
                            <div className="appt-table-related-info">
                                <img src={Images.relatedLink} alt="alert" />
                                <p>{related?.length}</p>
                            </div>
                        </button>
                    </SimpleTooltip>
                )}
                {refresh && !!issue?.length > 0 && (
                    <SimpleTooltip title={<p>{'Refresh Errors'}</p>} placement="bottom" arrow>
                        <button onClick={refreshError} className="reset-btn-by-default">
                            {loader?.length && selectedId === appt?.id ? (
                                <MinLoader margin={'0'} color={'#172B4D'} position={'relative'} />
                            ) : (
                                <img style={{ width: 20, height: 20 }} src={Images.refresh} alt="refresh" />
                            )}
                        </button>
                    </SimpleTooltip>
                )}
            </div>

            <Popover
                id={id}
                open={openAnchor}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                    zIndex: 1000,
                }}
            >
                {type === 'error' ? (
                    <div className="appt-popper-error-warning-box">
                        {issue?.map((i, j) => (
                            <div key={j} className="appt-popper-error-info-box">
                                <span>{renderApptStateIssues(i?.issueType, i?.description)}</span>
                                {i?.issueType === apptIssueType.DIAGNOSIS_MISMATCH && !!appt?.diagnosis?.clientCodes?.length && (
                                    <span>{`The client has ${appt.diagnosis.clientCodes.join(', ')}.`}</span>
                                )}
                            </div>
                        ))}
                    </div>
                ) : type === 'warning' ? (
                    <div className="appt-popper-error-warning-box">
                        {warningList?.map((i, j) => (
                            <div key={j} className="appt-popper-warning-info-box">
                                <span>{i?.text}</span>
                            </div>
                        ))}
                    </div>
                ) : type === 'notes' ? (
                    <div className="appt-popper-note-warning-box">
                        <div className="appt-popper-note-info-card">
                            <div className="appt-popper-note-info-box">
                                <div className="space-between" style={{ gap: 8 }}>
                                    <div className="flex-align-center" style={{ gap: 8 }}>
                                        <img src={Images.toolClinicalNote} alt="note-icon" />
                                        <p>Clinical Notes</p>
                                    </div>
                                    <p style={{ fontWeight: 600 }}>{clinicalNotesCount}</p>
                                </div>
                            </div>
                        </div>
                        <div className="appt-popper-note-info-card">
                            <div className="appt-popper-note-info-box">
                                <div className="space-between" style={{ gap: 8 }}>
                                    <div className="flex-align-center" style={{ gap: 8 }}>
                                        <img src={Images.toolOfficeNote} alt="note-icon" />
                                        <p>Office Notes</p>
                                    </div>
                                    <p style={{ fontWeight: 600 }}>{officeNotesCount}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                ) : type === 'related' ? (
                    <div className="appt-popper-error-warning-box" style={{ width: 200 }}>
                        {related?.map((i, j) => (
                            <div
                                className="appt-popper-related-info-box"
                                onClick={(e) => {
                                    handleClose(e);
                                    openSecondary(<ScheduleDetails currentId={i?.id} />);
                                }}
                                key={j}
                            >
                                {i?.displayId}
                            </div>
                        ))}
                    </div>
                ) : null}
            </Popover>
        </div>
    );
};
